import { ThemeEvents } from '@theme/events';

/**
 * PDP shipping calculator (blocks/shipping-calculator.liquid).
 *
 * Quotes real checkout shipping rates for the selected variant using Shopify's cart AJAX
 * shipping-rates endpoint — the same rating engine (carrier apps like ShipStation included)
 * the checkout uses. That endpoint rates the *current cart*, so to quote shipping for THIS
 * product alone (independent of whatever else the shopper already has in their cart) we
 * temporarily reduce the cart to a single unit of the variant, read the rates, then restore
 * the shopper's cart to its previous state. No app or API token required.
 */

const formatter = new Intl.NumberFormat('en-AU', { style: 'currency', currency: 'AUD' });

/** Australian state from a 4-digit postcode — Shopify needs a province for AU rates. */
function provinceFromPostcode(postcode) {
  const code = Number(postcode);
  if (code >= 800 && code <= 999) return 'NT';
  if ((code >= 2600 && code <= 2618) || (code >= 2900 && code <= 2920)) return 'ACT';
  if (code >= 1000 && code <= 2999) return 'NSW';
  if ((code >= 3000 && code <= 3999) || (code >= 8000 && code <= 8999)) return 'VIC';
  if ((code >= 4000 && code <= 4999) || (code >= 9000 && code <= 9999)) return 'QLD';
  if (code >= 5000 && code <= 5999) return 'SA';
  if (code >= 6000 && code <= 6999) return 'WA';
  if (code >= 7000 && code <= 7999) return 'TAS';
  return null;
}

function getSelectedVariantId(fallbackId) {
  const urlVariant = new URLSearchParams(window.location.search).get('variant');
  if (urlVariant) return urlVariant;
  const formInput = document.querySelector('.product-details form[action*="/cart/add"] input[name="id"]');
  return formInput?.value || fallbackId;
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url} → ${response.status}`);
  return response.json();
}

class ShippingCalculator {
  constructor(root) {
    this.root = root;
    this.fallbackVariantId = root.dataset.variantId;
    this.input = root.querySelector('.shipping-calculator__input:not(.shipping-calculator__suburb-input)');
    this.suburbInput = root.querySelector('.shipping-calculator__suburb-input');
    this.button = root.querySelector('.shipping-calculator__button');
    this.results = root.querySelector('.shipping-calculator__results');
    if (!this.input || !this.button || !this.results) return;

    this.button.addEventListener('click', () => this.calculate());
    const submitOnEnter = (event) => {
      if (event.key === 'Enter') {
        event.preventDefault();
        this.calculate();
      }
    };
    this.input.addEventListener('keydown', submitOnEnter);
    this.suburbInput?.addEventListener('keydown', submitOnEnter);
    // Rates are variant-specific (weight/SKU); a stale quote would mislead.
    document.addEventListener(ThemeEvents.variantUpdate, () => this.showMessage(''));
  }

  showMessage(text) {
    this.results.textContent = text;
  }

  showRates(rates) {
    this.results.textContent = '';
    const list = document.createElement('ul');
    list.className = 'shipping-calculator__rates';
    for (const rate of rates) {
      const item = document.createElement('li');
      item.className = 'shipping-calculator__rate';

      const info = document.createElement('span');
      info.className = 'shipping-calculator__rate-info';
      const name = document.createElement('span');
      name.className = 'shipping-calculator__rate-name';
      name.textContent = rate.name || rate.presentment_name || 'Shipping';
      info.append(name);

      const amount = Number(rate.price);
      const price = document.createElement('span');
      price.className = 'shipping-calculator__rate-price';
      price.textContent = amount === 0 ? 'FREE' : formatter.format(amount);

      item.append(info, price);
      list.append(item);
    }
    this.results.append(list);
  }

  async quoteRates(zip, province, city) {
    const params = new URLSearchParams({
      'shipping_address[zip]': zip,
      'shipping_address[country]': 'AU',
      'shipping_address[province]': province,
    });
    // Required — some carrier-calculated rates zone by suburb, not just postcode (a postcode can
    // straddle a metro/remote split), so quoting without it can be materially wrong.
    params.set('shipping_address[city]', city);
    const data = await fetch(`/cart/shipping_rates.json?${params}`, {
      headers: { Accept: 'application/json' },
    }).then((r) => {
      if (!r.ok) throw new Error(`shipping_rates → ${r.status}`);
      return r.json();
    });
    return data.shipping_rates || [];
  }

  async fetchRates(variantId, zip, province, city) {
    // shipping_rates.json rates the *current cart*, so any other items the shopper already has
    // (their weight, quantities, free-shipping thresholds) would skew the quote and make the
    // same product estimate differently depending on cart contents. Quote against a cart holding
    // exactly one unit of this variant, then restore the shopper's cart unchanged.
    const before = await fetch('/cart.js', { headers: { Accept: 'application/json' } }).then((r) => r.json());

    const alreadyIsolated =
      before.item_count === 1 &&
      String(before.items[0].variant_id) === String(variantId) &&
      before.items[0].quantity === 1;
    if (alreadyIsolated) {
      return this.quoteRates(zip, province, city);
    }

    // Clear first, then add inside the try so restoreCart() runs even if the add itself fails
    // (e.g. the variant is sold out) — never leave the shopper's cart cleared.
    await postJson('/cart/clear.js', {});
    try {
      await postJson('/cart/add.js', { id: variantId, quantity: 1 });
      return await this.quoteRates(zip, province, city);
    } finally {
      await this.restoreCart(before);
    }
  }

  /** Restore the cart to a /cart.js snapshot, preserving quantities, properties and selling plans. */
  async restoreCart(snapshot) {
    await postJson('/cart/clear.js', {});
    if (!snapshot.items.length) return;
    await postJson('/cart/add.js', {
      items: snapshot.items.map((item) => ({
        id: item.variant_id,
        quantity: item.quantity,
        properties: item.properties,
        selling_plan: item.selling_plan_allocation?.selling_plan?.id,
      })),
    });
  }

  async calculate() {
    const suburb = this.suburbInput?.value.trim() || '';
    if (!suburb) {
      this.showMessage('Please enter a suburb.');
      return;
    }
    const postcode = this.input.value.trim();
    if (!/^\d{4}$/.test(postcode)) {
      this.showMessage('Please enter a valid 4-digit postcode.');
      return;
    }
    const province = provinceFromPostcode(postcode);
    if (!province) {
      this.showMessage('Please enter a valid Australian postcode.');
      return;
    }
    const variantId = getSelectedVariantId(this.fallbackVariantId);
    if (!variantId) {
      this.showMessage('Unable to determine the selected product option.');
      return;
    }
    this.button.disabled = true;
    this.showMessage('Calculating…');
    try {
      const rates = await this.fetchRates(variantId, postcode, province, suburb);
      if (rates.length) {
        this.showRates(rates);
      } else {
        this.showMessage('No shipping rates found for that postcode — contact us for a quote.');
      }
    } catch (error) {
      console.error('[shipping-calculator]', error);
      this.showMessage('Sorry, we could not calculate shipping right now. Please try again.');
    } finally {
      this.button.disabled = false;
    }
  }
}

function init() {
  for (const root of document.querySelectorAll('.shipping-calculator[data-variant-id]')) {
    new ShippingCalculator(root);
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init, { once: true });
} else {
  init();
}
